interface IPayment {
	id: string
	value: number
	status?: string
	dueDate?: string
	customer?: PaymentCustomer
	professional?: PaymentProfessional
	treatment?: PaymentTreatment
	targetModel?: string
	paymentMethod?: string
	paymentDate?: string
	payerCpf?: string
	file?: { url: string }
	type?: string
	fine?: number
	clinic?: string
	withNote?: boolean
	asaas?: PaymentAsaas
}

export class Payment implements IPayment {
	static modelName = 'Payment'

	id: string
	value: number
	status?: string
	dueDate?: string
	customer?: PaymentCustomer
	professional?: PaymentProfessional
	targetModel?: string
	treatment?: PaymentTreatment
	paymentMethod?: string
	paymentDate?: string
	payerCpf?: string
	file?: { url: string }
	type?: string
	fine?: number
	clinic?: string
	withNote?: boolean
	asaas?: PaymentAsaas

	constructor(object: IPayment) {
		Object.assign(this, object)
	}

	static fromJson(json: Record<any, any>) {
		return new Payment({
			id: json._id,
			withNote: json.withNote,
			value: json.value,
			status: json.status,
			type: json.type,
			fine: json.fine,
			dueDate: json.dueDate,
			paymentDate: json.paymentDate,
			payerCpf: json.payerCpf,
			file: json.file,
			paymentMethod: json.paymentMethod,
			targetModel: json.targetModel,
			customer: json.customer && {
				id: json.customer._id,
				name: json.customer.name,
				cpf: json.customer.cpf,
				legalRepresentatives: json.customer.legalRepresentatives,
			},
			professional: json.dentist && {
				id: json.dentist._id,
				name: json.dentist.name,
			},
			treatment: json.treatment && { id: json.treatment._id },
			clinic: json.clinic,
			asaas: json.asaas,
		})
	}
}

interface PaymentAsaas {
	paymentId?: string
	bankSlipUrl?: string
	invoiceUrl?: string
	identificationField?: string
	status?: string
	interestValue?: number
	netValue?: number
}

interface PaymentProfessional {
	id: string
	name: string
}

interface PaymentCustomer {
	id: string
	name: string
	cpf: string
	legalRepresentatives?: { name?: string; cpf?: string }[]
}

const onlyDigits = (s?: string) => String(s ?? '').replace(/\D/g, '')

// Who is financially responsible for a payment, for fiscal/accounting reports.
// A minor's payment must show the guardian even when the boleto was issued in the
// patient's own name (Luciano, WhatsApp 2026-10-08). Guardian whose CPF matches
// payerCpf wins, else the first guardian; without a guardian, the patient.
// Mirrors financialResponsible() in consultdata-api report.helpers.js.
export function getFinancialResponsible(
	payment: Pick<IPayment, 'customer' | 'payerCpf'>
): { name: string; cpf: string } {
	const customer = payment.customer
	const reps = (customer?.legalRepresentatives ?? []).filter(
		(r) => r && (r.name || r.cpf)
	)
	if (reps.length) {
		const payer = onlyDigits(payment.payerCpf)
		const rep =
			(payer && reps.find((r) => onlyDigits(r.cpf) === payer)) || reps[0]
		return { name: rep.name ?? '', cpf: onlyDigits(rep.cpf) }
	}
	return { name: customer?.name ?? '', cpf: onlyDigits(customer?.cpf) }
}

// CPF shown as "CPF Pagador" in reports: the CPF recorded on the payment (boleto
// payer), else the financial responsible's. Mirrors the API accounting export
// ('CPF Pagador' = payerCpf || responsible.cpf). Digits only, '' when unknown.
export function getPayerCpf(
	payment: Pick<IPayment, 'customer' | 'payerCpf'>
): string {
	return onlyDigits(payment.payerCpf) || getFinancialResponsible(payment).cpf
}

interface PaymentTreatment {
	id: string
}
